// ScheduleGradesPosts.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * Props:
 *   - platformUrl (required): Learn base URL, e.g., https://your-learn.example.com
 *   - backEndUrl (optional): your API for scheduling
 *   - tenantId (optional but recommended when backEndUrl is set)
 *
 * Auth:
 *   - token from URL (?token=...)
 *   - courseId from localStorage("courseId")
 *   - tenantId from prop OR ?tenantId=... OR localStorage("tenantId")
 */

const GRADE_EVENT_POSTED = "EventKeyPostGrade";
const GRADE_EVENT_NOT_POSTED = "EventKeyNotPostedGrade";
const SUBMIT_EVENT = "EventKeySubmit";

const TENANT_HEADER = "x-tenant-id";

const ScheduleGradesPosts = ({ platformUrl, backEndUrl, tenantId: tenantIdProp }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [filter, setFilter] = useState("");
    const [sort, setSort] = useState({ key: "name", dir: "asc" });

    // per-column UI state
    const [scheduledAt, setScheduledAt] = useState({}); // { [colId]: "YYYY-MM-DDTHH:mm" }
    const [scheduleBusy, setScheduleBusy] = useState({});
    const [runBusy, setRunBusy] = useState({});
    const [scheduleMsg, setScheduleMsg] = useState({});

    // chosen schedule per column (deduped)
    // shape: { [colId]: { id, status, scheduleAtIso?, lastPostedAt?, count } }
    const [schedulesByColumn, setSchedulesByColumn] = useState({});

    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const tenantIdFromQs = params.get("tenantId") || params.get("tenant") || "";
    const courseId = localStorage.getItem("courseId");
    const tenantIdLocal = localStorage.getItem("tenantId") || "";
    const tenantId = (tenantIdProp || tenantIdFromQs || tenantIdLocal || "").trim();

    useEffect(() => {
        const run = async () => {
            if (!token) {
                setErr("Missing access token (?token=...).");
                setLoading(false);
                return;
            }
            if (!courseId) {
                setErr(
                    "Missing courseId in localStorage. Set localStorage.setItem('courseId','<_123_1>') and reload."
                );
                setLoading(false);
                return;
            }
            if (!platformUrl) {
                setErr("Missing platformUrl prop (e.g., https://your-learn.example.com).");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setErr("");
                const auth = { headers: { Authorization: `Bearer ${token}` } };

                // 1) Columns
                const cols = await fetchAllPaged(
                    `${platformUrl}/learn/api/public/v2/courses/${encodeURIComponent(
                        courseId
                    )}/gradebook/columns?limit=200&fields=id,name,grading.due`,
                    auth
                );

                // 2) Enrich with logs
                const enriched = await mapWithConcurrency(cols, 6, async (c) => {
                    const colId = c?.id;
                    const dueIso = c?.grading?.due || null;

                    let logs = [];
                    if (colId) {
                        logs = await fetchAllPaged(
                            `${platformUrl}/learn/api/public/v1/courses/${encodeURIComponent(
                                courseId
                            )}/gradebook/columns/${encodeURIComponent(
                                colId
                            )}/logs?limit=200&fields=event,user.userId,created`,
                            auth
                        );
                    }

                    const submittedAttempts = (logs || []).filter((e) => e?.event === SUBMIT_EVENT).length;

                    const gradeEvents = (logs || []).filter(
                        (e) => e?.event === GRADE_EVENT_NOT_POSTED || e?.event === GRADE_EVENT_POSTED
                    );

                    let gradesNotPosted = 0;
                    if (gradeEvents.length) {
                        const hasCreated = gradeEvents.some((e) => !!e?.created);
                        if (hasCreated) {
                            const lastByUser = new Map();
                            gradeEvents
                                .slice()
                                .sort((a, b) => new Date(a.created) - new Date(b.created))
                                .forEach((e) => {
                                    const uid = e?.user?.userId || "";
                                    if (!uid) return;
                                    lastByUser.set(uid, e?.event);
                                });
                            gradesNotPosted = Array.from(lastByUser.values()).filter(
                                (ev) => ev === GRADE_EVENT_NOT_POSTED
                            ).length;
                        } else {
                            const postedUsers = new Set(
                                gradeEvents
                                    .filter((e) => e?.event === GRADE_EVENT_POSTED)
                                    .map((e) => e?.user?.userId)
                            );
                            const notPostedUsers = new Set(
                                gradeEvents
                                    .filter((e) => e?.event === GRADE_EVENT_NOT_POSTED && !postedUsers.has(e?.user?.userId))
                                    .map((e) => e?.user?.userId)
                            );
                            gradesNotPosted = notPostedUsers.size;
                        }
                    }

                    return {
                        id: colId || c?.name || Math.random().toString(36).slice(2),
                        name: c?.name || "(untitled column)",
                        dueIso,
                        dueDisp: formatDate(dueIso),
                        submittedAttempts,
                        gradesNotPosted,
                    };
                });

                setRows(enriched);

                // 3) Load schedules to enable "Run now" and pre-fill inputs
                if (backEndUrl) {
                    await loadSchedulesForCourse({
                        backEndUrl,
                        token,
                        courseId,
                        tenantId,
                        setSchedulesByColumn,
                    });
                }
            } catch (e) {
                setErr(e?.response?.data?.message || e?.message || String(e));
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [platformUrl, backEndUrl, token, courseId, tenantId]);

    // Pre-fill datetime-local from chosen schedules (don’t override user edits)
    useEffect(() => {
        if (!rows?.length || !schedulesByColumn) return;
        setScheduledAt((prev) => {
            const next = { ...prev };
            for (const r of rows) {
                const colId = r.id;
                if (!colId || next[colId]) continue;
                const sched = schedulesByColumn[colId];
                const iso = sched?.scheduleAtIso || null; // only future “Scheduled” or “Overdue” carry this
                if (iso) next[colId] = isoToLocalDatetimeInput(iso);
            }
            return next;
        });
    }, [rows, schedulesByColumn]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        const base = q ? rows.filter((r) => r.name.toLowerCase().includes(q)) : rows;

        const { key, dir } = sort;
        const sign = dir === "asc" ? 1 : -1;

        return [...base].sort((a, b) => {
            if (key === "due") {
                const av = a.dueIso || "";
                const bv = b.dueIso || "";
                if (!av && !bv) return 0;
                if (!av) return dir === "asc" ? 1 : -1;
                if (!bv) return dir === "asc" ? -1 : 1;
                if (av < bv) return -1 * sign;
                if (av > bv) return 1 * sign;
                return 0;
            }
            if (key === "submittedAttempts" || key === "gradesNotPosted") {
                const av = Number(a[key] || 0);
                const bv = Number(b[key] || 0);
                if (av < bv) return -1 * sign;
                if (av > bv) return 1 * sign;
                return 0;
            }
            const av = (a[key] ?? "").toString().toLowerCase();
            const bv = (b[key] ?? "").toString().toLowerCase();
            if (av < bv) return -1 * sign;
            if (av > bv) return 1 * sign;
            return 0;
        });
    }, [rows, filter, sort]);

    const onSort = (key) => {
        setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
    };

    function tenantHeaders(base = {}) {
        const h = { ...base };
        if (tenantId) h[TENANT_HEADER] = tenantId;
        return h;
    }

    async function handleSchedule(row) {
        const colId = row.id;
        const value = (scheduledAt[colId] || "").trim();
        const localDate = parseLocalDatetime(value);

        if (!value || !localDate || isNaN(localDate.getTime())) {
            setScheduleMsg((m) => ({ ...m, [colId]: "Please pick a valid date/time." }));
            return;
        }
        if (localDate.getTime() <= Date.now()) {
            setScheduleMsg((m) => ({ ...m, [colId]: "Please choose a future date/time." }));
            return;
        }

        try {
            setScheduleBusy((b) => ({ ...b, [colId]: true }));
            setScheduleMsg((m) => ({ ...m, [colId]: "" }));

            const payload = {
                courseId,
                columnId: colId,
                columnName: row.name,
                scheduleAtIso: localDate.toISOString(), // store as UTC ISO
            };

            if (backEndUrl) {
                if (!tenantId) {
                    setScheduleMsg((m) => ({
                        ...m,
                        [colId]:
                            "Tenant id is required for backend scheduling. Provide tenantId prop, ?tenantId=..., or localStorage('tenantId').",
                    }));
                    return;
                }

                const existing = schedulesByColumn[colId];
                const hasExisting = !!existing?.id;

                if (hasExisting) {
                    // PATCH existing schedule by ID (avoids creating multiple records)
                    await axios.patch(
                        `${backEndUrl}/gradebook/schedule-post/${encodeURIComponent(
                            existing.id
                        )}?platformUrl=${encodeURIComponent(platformUrl)}`,
                        payload,
                        {
                            headers: tenantHeaders({
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            }),
                        }
                    );
                    setScheduleMsg((m) => ({ ...m, [colId]: "Updated ✔" }));
                } else {
                    // Create new schedule
                    await axios.post(
                        `${backEndUrl}/gradebook/schedule-post?platformUrl=${encodeURIComponent(platformUrl)}`,
                        payload,
                        {
                            headers: tenantHeaders({
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            }),
                        }
                    );
                    setScheduleMsg((m) => ({ ...m, [colId]: "Scheduled ✔" }));
                }

                // Refresh from backend so we maintain a single record per course+column
                await loadSchedulesForCourse({ backEndUrl, token, courseId, tenantId, setSchedulesByColumn });
            } else {
                // Local fallback (no backend)
                const key = `sgp:schedules:${courseId}`;
                const existing = JSON.parse(localStorage.getItem(key) || "[]");
                const withoutThis = existing.filter((it) => it.columnId !== colId); // keep unique per course+column locally
                const newItem = { ...payload, platformUrl, savedAt: new Date().toISOString() };
                localStorage.setItem(key, JSON.stringify([...withoutThis, newItem]));
                setScheduleMsg((m) => ({ ...m, [colId]: "Saved locally (no backend configured)." }));
            }
        } catch (e) {
            setScheduleMsg((m) => ({
                ...m,
                [colId]: `Failed to schedule: ${e?.response?.data?.message || e?.message || "Unknown error"}`,
            }));
        } finally {
            setScheduleBusy((b) => ({ ...b, [colId]: false }));
        }
    }

    async function handleRunNow(row) {
        const colId = row.id;

        try {
            setRunBusy((b) => ({ ...b, [colId]: true }));
            setScheduleMsg((m) => ({ ...m, [colId]: "" }));

            const chosen = schedulesByColumn[colId];
            if (backEndUrl && chosen?.id && chosen?.scheduleAtIso) {
                if (!tenantId) {
                    setScheduleMsg((m) => ({
                        ...m,
                        [colId]:
                            "Tenant id is required to run via backend. Provide tenantId prop, ?tenantId=..., or localStorage('tenantId').",
                    }));
                    return;
                }

                await axios.post(
                    `${backEndUrl}/gradebook/schedule-post/${encodeURIComponent(chosen.id)}/run?platformUrl=${encodeURIComponent(
                        platformUrl
                    )}`,
                    {},
                    { headers: tenantHeaders({ Authorization: `Bearer ${token}` }) }
                );
                setScheduleMsg((m) => ({ ...m, [colId]: "Run requested ✔ (backend)" }));
                return;
            }

            // Direct fallback
            await axios.post(
                `${platformUrl}/learn/api/v1/courses/${encodeURIComponent(courseId)}/gradebook/columns/${encodeURIComponent(
                    colId
                )}/postReadyGrades?allStudents=true`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setScheduleMsg((m) => ({ ...m, [colId]: "Grades posted ✔" }));
        } catch (e) {
            setScheduleMsg((m) => ({
                ...m,
                [colId]: `Run failed: ${e?.response?.data?.message || e?.message || "Unknown error"}`,
            }));
        } finally {
            setRunBusy((b) => ({ ...b, [colId]: false }));
        }
    }

    if (err) {
        return (
            <div className="bb-container bb-error">
                <h1 className="bb-title">Error</h1>
                <p>{err}</p>
                <p>Please verify your access token and courseId.</p>
            </div>
        );
    }

    if (loading) {
        return <div className="bb-container">Loading gradebook columns…</div>;
    }

    return (
        <div className="bb-container">
            <div style={{ margin: "0 0 12px 0" }}>
                <input
                    className="bb-input"
                    placeholder="Filter by column name…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{ minWidth: 280 }}
                />
                <span style={{ marginLeft: 12, opacity: 0.7 }}>
          Showing {filtered.length} of {rows.length}
        </span>
                {backEndUrl && !tenantId && (
                    <span style={{ marginLeft: 12, color: "#a94442" }}>
            (tenantId required for backend operations)
          </span>
                )}
            </div>

            <table className="bb-table">
                <thead>
                <tr>
                    <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => onSort("name")} title="Sort by Name">
                        Name{sort.key === "name" ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                    <th style={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => onSort("due")} title="Sort by Due Date">
                        Due Date{sort.key === "due" ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                    <th
                        style={{ cursor: "pointer", whiteSpace: "nowrap", textAlign: "center" }}
                        onClick={() => onSort("submittedAttempts")}
                        title="Sort by Submitted Attempts"
                    >
                        Submitted Attempts{sort.key === "submittedAttempts" ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                    <th
                        style={{ cursor: "pointer", whiteSpace: "nowrap", textAlign: "center" }}
                        onClick={() => onSort("gradesNotPosted")}
                        title="Sort by Grades Not Posted"
                    >
                        Grades Not Posted{sort.key === "gradesNotPosted" ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </th>
                    <th style={{ whiteSpace: "nowrap" }}>Schedule Posting</th>
                </tr>
                </thead>
                <tbody>
                {filtered.map((r) => {
                    const colId = r.id;
                    const busy = !!scheduleBusy[colId];
                    const running = !!runBusy[colId];
                    const msg = scheduleMsg[colId];
                    const value = scheduledAt[colId] ?? "";
                    const min = nowLocalDatetime();

                    const sched = schedulesByColumn[colId];
                    const canRunViaBackend = !!(backEndUrl && sched?.id && sched?.scheduleAtIso);

                    return (
                        <tr key={colId}>
                            <td>{r.name}</td>
                            <td>{r.dueDisp || "—"}</td>
                            <td style={{ textAlign: "center" }}>{r.submittedAttempts}</td>
                            <td style={{ textAlign: "center" }}>{r.gradesNotPosted}</td>
                            <td>
                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                                    <input
                                        type="datetime-local"
                                        className="bb-input"
                                        value={value}
                                        min={min}
                                        onChange={(e) => setScheduledAt((s) => ({ ...s, [colId]: e.target.value }))}
                                        style={{ minWidth: 230 }}
                                    />
                                    <button
                                        className="bb-button"
                                        disabled={busy}
                                        onClick={() => handleSchedule(r)}
                                        title={
                                            backEndUrl
                                                ? "Create or update the schedule on your backend"
                                                : "Save locally (no backend configured)"
                                        }
                                    >
                                        {busy ? "Saving…" : sched?.id ? "Update" : "Schedule"}
                                    </button>
                                    <button
                                        className="bb-button"
                                        disabled={running}
                                        onClick={() => handleRunNow(r)}
                                        title={
                                            canRunViaBackend
                                                ? "Trigger backend run now for this schedule"
                                                : "Run now without schedule (direct Learn API)"
                                        }
                                    >
                                        {running ? "Running…" : "Run now"}
                                    </button>
                                </div>

                                {/* status line */}
                                {backEndUrl && sched && (
                                    <div style={{ fontSize: 12, marginTop: 6, opacity: 0.8 }}>
                                        {sched.status === "Scheduled" && sched.scheduleAtIso && <>Scheduled for: {formatDate(sched.scheduleAtIso)}</>}
                                        {sched.status === "Overdue" && sched.scheduleAtIso && <>Past scheduled time: {formatDate(sched.scheduleAtIso)}</>}
                                        {sched.status === "Completed" && sched.lastPostedAt && <>Last posted: {formatDate(sched.lastPostedAt)}</>}
                                        {typeof sched.count === "number" && sched.count > 1 && <> &nbsp;• {sched.count} records</>}
                                    </div>
                                )}

                                {msg && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>{msg}</div>}
                            </td>
                        </tr>
                    );
                })}
                {filtered.length === 0 && (
                    <tr>
                        <td colSpan={5} style={{ textAlign: "center", opacity: 0.7 }}>
                            No columns match your filter.
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
        </div>
    );
};

/* ---------------- helpers ---------------- */

async function loadSchedulesForCourse({ backEndUrl, token, courseId, tenantId, setSchedulesByColumn }) {
    try {
        const res = await axios.get(
            `${backEndUrl}/gradebook/schedule-post?courseId=${encodeURIComponent(courseId)}`,
            { headers: tenantAwareHeaders(token, tenantId) }
        );
        const rawItems = res?.data?.items || res?.data?.results || res?.data || [];
        const items = Array.isArray(rawItems) ? rawItems : [];

        // group by ColumnId and choose best record
        const byCol = {};
        const groups = new Map();
        for (const it of items) {
            const columnId = it.columnId || it.ColumnId || it.colId || it.columnID;
            if (!columnId) continue;
            const id = it.id || it.ID || it.Id || null;
            const status = String(it.status || it.Status || "").toLowerCase();
            const scheduleAtIso = it.scheduleAtIso || it.ScheduleAtIso || null;
            const createdAt = it.createdAt || it.CreatedAt || null;
            const postedAt = it.postedAt || it.PostedAt || null;

            const rec = { id, columnId, status, scheduleAtIso, createdAt, postedAt, _raw: it };
            const arr = groups.get(columnId) || [];
            arr.push(rec);
            groups.set(columnId, arr);
        }

        const now = Date.now();
        for (const [colId, arr] of groups.entries()) {
            // normalize & choose
            const norm = (s) => (s === "completed" ? "Completed" : s === "scheduled" ? "Scheduled" : (s || "").toString());
            const toTs = (iso) => {
                const d = iso ? new Date(iso) : null;
                return d && !isNaN(d.getTime()) ? d.getTime() : null;
            };

            const scheduled = arr.filter((r) => norm(r.status) === "Scheduled");
            const future = scheduled.filter((r) => (toTs(r.scheduleAtIso) ?? 0) >= now);
            const past = scheduled.filter((r) => {
                const ts = toTs(r.scheduleAtIso);
                return ts !== null && ts < now;
            });

            const sortBySchedDesc = (x, y) =>
                (toTs(y.scheduleAtIso) ?? 0) - (toTs(x.scheduleAtIso) ?? 0) ||
                (toTs(y.createdAt) ?? 0) - (toTs(x.createdAt) ?? 0);
            const sortByCreatedDesc = (x, y) =>
                (toTs(y.createdAt) ?? 0) - (toTs(x.createdAt) ?? 0) ||
                (toTs(y.postedAt) ?? 0) - (toTs(x.postedAt) ?? 0);

            let chosen = null;
            let statusLabel = null;

            if (future.length) {
                future.sort(sortBySchedDesc);
                chosen = future[0];
                statusLabel = "Scheduled";
            } else if (past.length) {
                past.sort(sortBySchedDesc);
                chosen = past[0];
                statusLabel = "Overdue";
            } else {
                const completed = arr.filter((r) => norm(r.status) === "Completed");
                if (completed.length) {
                    completed.sort(sortByCreatedDesc);
                    chosen = completed[0];
                    statusLabel = "Completed";
                }
            }

            if (chosen) {
                byCol[colId] = {
                    id: chosen.id || null,
                    status: statusLabel,
                    scheduleAtIso: chosen.scheduleAtIso || null,
                    lastPostedAt: chosen.postedAt || chosen.createdAt || null,
                    count: arr.length,
                };
            }
        }

        setSchedulesByColumn(byCol);
    } catch {
        setSchedulesByColumn({});
    }
}

function tenantAwareHeaders(token, tenantId) {
    const h = { Authorization: `Bearer ${token}` };
    if (tenantId) h[TENANT_HEADER] = tenantId;
    return h;
}

async function fetchAllPaged(firstUrl, axiosConfig) {
    const all = [];
    let url = firstUrl;
    while (url) {
        const res = await axios.get(url, axiosConfig);
        const data = res?.data || {};
        const items = Array.isArray(data.results) ? data.results : [];
        all.push(...items);
        let next = data?.paging?.nextPage;
        if (next && !/^https?:\/\//i.test(next)) {
            const base = new URL(res.config.url);
            next = `${base.origin}${next}`;
        }
        url = next || null;
    }
    return all;
}

async function mapWithConcurrency(list, concurrency, mapper) {
    const results = [];
    let i = 0;
    const workers = Array.from({ length: Math.max(1, concurrency) }, async () => {
        while (i < list.length) {
            const idx = i++;
            try {
                results[idx] = await mapper(list[idx], idx);
            } catch {
                results[idx] = null;
            }
        }
    });
    await Promise.all(workers);
    return results.filter(Boolean);
}

function formatDate(isoMaybe) {
    if (!isoMaybe) return "";
    try {
        const d = new Date(isoMaybe);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleString();
    } catch {
        return "";
    }
}

function nowLocalDatetime() {
    const d = new Date();
    d.setSeconds(0, 0);
    return toDatetimeLocalValue(d);
}

function toDatetimeLocalValue(dateObj) {
    const pad = (n) => String(n).padStart(2, "0");
    const y = dateObj.getFullYear();
    const m = pad(dateObj.getMonth() + 1);
    const d = pad(dateObj.getDate());
    const hh = pad(dateObj.getHours());
    const mm = pad(dateObj.getMinutes());
    return `${y}-${m}-${d}T${hh}:${mm}`;
}

function parseLocalDatetime(str) {
    if (!str) return null;
    const [datePart, timePart] = str.split("T");
    if (!datePart || !timePart) return null;
    const [y, m, d] = datePart.split("-").map((v) => parseInt(v, 10));
    const [hh, mm] = timePart.split(":").map((v) => parseInt(v, 10));
    if ([y, m, d, hh, mm].some((v) => Number.isNaN(v))) return null;
    return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0);
}

function isoToLocalDatetimeInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${y}-${m}-${day}T${hh}:${mm}`;
}

export default ScheduleGradesPosts;
